import { Injectable, computed, inject, signal } from '@angular/core';
import { filter, first, skip } from 'rxjs/operators';
import { WaypointService } from './waypoint.service';
import { StackedModalsService } from '../modal/stacked-modals.service';
import { DisplayMode } from '../../models/display-mode';

/**
 * Steps of the guided tutorial, in order. Each one points at a piece of the
 * live UI and waits for the user to actually perform the action — there is no
 * "Next" button, the app's own state changes move the flow forward.
 */
export enum TutorialStep {
  /** Long-press the map and confirm the POI card to plan a route. */
  AddWaypoint = 'add-waypoint',
  /** Press GO on the planning panel. */
  StartNavigation = 'start-navigation',
  /** Press the stop (X) button on the navigation card. */
  StopNavigation = 'stop-navigation',
  /** Press the microphone button. */
  VoiceInput = 'voice-input',
  /** Everything done — closing card with a Finish button. */
  Done = 'done',
}

/** Where the coach mark sits relative to its target; `center` floats mid-screen with no target. */
export type TutorialPlacement = 'top' | 'bottom' | 'left' | 'right' | 'center';

export interface TutorialStepDefinition {
  step: TutorialStep;
  /** Matches the `data-tutorial-target` attribute of the element to point at. Omitted for floating cards. */
  target?: string;
  /**
   * `data-tutorial-container` of the panel the target sits in. The card is
   * kept outside of it, so it never covers the panel's other controls.
   */
  container?: string;
  /** Preferred placement; the overlay flips it when it does not fit on screen. */
  placement: TutorialPlacement;
  /**
   * Shown while `target` is not on screen: the card points at this element
   * instead (or floats mid-screen when it is missing too) with its own copy.
   * Lets one step first ask for the action that reveals the real target and
   * then point at it. Steps without a fallback float with their regular copy
   * until the target shows up.
   */
  fallback?: {
    target?: string;
    container?: string;
    placement: TutorialPlacement;
    titleKey: string;
    textKey: string;
  };
  /** Advance as soon as the target element receives a click (for actions with no observable app state change). */
  completeOnTargetClick?: boolean;
  titleKey: string;
  textKey: string;
}

/** The interactive steps in order — used for the "1/4" progress indicator. */
export const TUTORIAL_STEPS: TutorialStepDefinition[] = [
  {
    // Points at the search box (search or a map long-press both draw the POI
    // card), then at the POI card's GO button once it is on the map.
    step: TutorialStep.AddWaypoint,
    target: 'poi-go',
    placement: 'right',
    fallback: {
      target: 'search',
      container: 'search-panel',
      placement: 'right',
      titleKey: 'TutorialFlow_AddWaypoint_Title',
      textKey: 'TutorialFlow_AddWaypoint_Text',
    },
    titleKey: 'TutorialFlow_PoiGo_Title',
    textKey: 'TutorialFlow_PoiGo_Text',
  },
  {
    step: TutorialStep.StartNavigation,
    target: 'start-navigation',
    container: 'planning-panel',
    placement: 'right',
    titleKey: 'TutorialFlow_StartNavigation_Title',
    textKey: 'TutorialFlow_StartNavigation_Text',
  },
  {
    step: TutorialStep.StopNavigation,
    target: 'stop-navigation',
    placement: 'top',
    titleKey: 'TutorialFlow_StopNavigation_Title',
    textKey: 'TutorialFlow_StopNavigation_Text',
  },
  {
    step: TutorialStep.VoiceInput,
    target: 'voice-input',
    placement: 'top',
    completeOnTargetClick: true,
    titleKey: 'TutorialFlow_VoiceInput_Title',
    textKey: 'TutorialFlow_VoiceInput_Text',
  },
];

export const TUTORIAL_DONE_STEP: TutorialStepDefinition = {
  step: TutorialStep.Done,
  placement: 'center',
  titleKey: 'TutorialFlow_Done_Title',
  textKey: 'TutorialFlow_Done_Text',
};

/**
 * Owns the guided-tutorial state. The flow can only start from RANGE mode
 * (one start point, nothing planned) and follows the user through planning a
 * route, starting and stopping navigation and opening voice input. Display
 * mode changes drive the transitions, so whatever way the user performs the
 * action (map long-press, search, voice…) the tutorial keeps up — and if they
 * back out (cancel planning), it steps back too.
 */
@Injectable({ providedIn: 'root' })
export class TutorialService {
  private readonly waypointService = inject(WaypointService);
  private readonly stackedModalsService = inject(StackedModalsService);

  public readonly step = signal<TutorialStep | null>(null);
  public readonly active = computed(() => this.step() !== null);

  private readonly displayMode = signal<DisplayMode>(DisplayMode.RANGE);
  /** True when the tutorial may be started right now (RANGE mode, not navigating). */
  public readonly canStart = computed(() => this.displayMode() === DisplayMode.RANGE);

  public readonly definition = computed<TutorialStepDefinition | null>(() => {
    const step = this.step();
    if (step === null) return null;
    if (step === TutorialStep.Done) return TUTORIAL_DONE_STEP;
    return TUTORIAL_STEPS.find(d => d.step === step) ?? null;
  });

  /** 1-based index of the current interactive step (for "n / total"); the Done card keeps the last one. */
  public readonly stepIndex = computed(() => {
    const step = this.step();
    if (step === null) return 0;
    if (step === TutorialStep.Done) return TUTORIAL_STEPS.length;
    return TUTORIAL_STEPS.findIndex(d => d.step === step) + 1;
  });
  public readonly stepCount = TUTORIAL_STEPS.length;

  constructor() {
    this.waypointService.ObservableSelectedDisplayMode.subscribe((mode) => {
      if (mode) {
        this.displayMode.set(mode);
        this.onDisplayModeChanged(mode);
      }
    });
  }

  /** Starts (or restarts) the tutorial. Returns false when the app is not in RANGE mode. */
  public start(): boolean {
    if (!this.canStart()) {
      return false;
    }
    this.step.set(TutorialStep.AddWaypoint);
    return true;
  }

  /**
   * Starts once every open modal is closed. Used by the Welcome dialog: on
   * first launch the car selector is queued behind it, and a coach mark under
   * a modal backdrop would be pointless.
   */
  public startAfterModalsClosed(): void {
    if (!this.stackedModalsService.isModalOpen()) {
      this.start();
      return;
    }
    // BehaviorSubject replays the current length on subscribe; skip it and
    // wait for the emission that closes the stack for real.
    this.stackedModalsService.ObservableModalStackLength.pipe(
      skip(1),
      filter(length => length === 0),
      first()
    ).subscribe(() => this.start());
  }

  public stop(): void {
    this.step.set(null);
  }

  /** Called by the overlay when the current step's target was clicked (`completeOnTargetClick`). */
  public completeTargetClick(): void {
    if (this.definition()?.completeOnTargetClick) {
      this.advance();
    }
  }

  private advance(): void {
    const idx = TUTORIAL_STEPS.findIndex(d => d.step === this.step());
    if (idx === -1) return;
    this.step.set(idx + 1 < TUTORIAL_STEPS.length ? TUTORIAL_STEPS[idx + 1].step : TutorialStep.Done);
  }

  private onDisplayModeChanged(mode: DisplayMode): void {
    const step = this.step();
    if (step === null) return;

    switch (mode) {
      case DisplayMode.ROUTE:
        if (step === TutorialStep.AddWaypoint) {
          this.step.set(TutorialStep.StartNavigation);
        }
        break;
      case DisplayMode.NAVIGATION:
        if (step === TutorialStep.AddWaypoint || step === TutorialStep.StartNavigation) {
          this.step.set(TutorialStep.StopNavigation);
        }
        break;
      case DisplayMode.RANGE:
        // Navigation exit / arrive lands back in RANGE: move on. Cancelling
        // the planning does too, but from an earlier step: start over.
        if (step === TutorialStep.StopNavigation) {
          this.step.set(TutorialStep.VoiceInput);
        } else if (step === TutorialStep.StartNavigation) {
          this.step.set(TutorialStep.AddWaypoint);
        }
        break;
    }
  }
}
